var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import Base from "../../Base.js";
import WalkHelper from "../../helper/WalkHelper.js";
var StoreSync_default = (Target) => {
  var _a;
  return _a = class extends (Target || Base) {
    static get configurable() {
      return {
        /**
         * Configure with `true` to sync loaded data instead of replacing existing with a new dataset.
         *
         * By default (or when configured with `false`) assigning to `store.data` replaces the entire dataset
         * with a new one, creating all new records:
         *
         * ```javascript
         * store.data = [ { id : 1, name : 'Saitama' } ];
         *
         * const first = store.first;
         *
         * store.data = [ { id : 1, name : 'One-Punch man' } ];
         *
         * // store.first !== first;
         * ```
         *
         * When configured with `true` the new dataset is instead synced against the old, figuring out what was
         * added, removed and updated:
         *
         * ```javascript
         * store.data = [ { id : 1, name : 'Saitama' } ];
         *
         * const first = store.first;
         *
         * store.data = [ { id : 1, name : 'One-Punch man' } ];
         *
         * // store.first === first;
         * ```
         *
         * After the sync, any configured sorters, groupers and filters will be reapplied.
         *
         * #### Threshold
         *
         * The sync operation has a configurable threshold, above which the operation will be treated as a
         * batch/refresh and only trigger a single `refresh` event. If threshold is not reached, individual events
         * will be triggered (single `add`, `remove` and possible multiple `update`). To enable the threshold,
         * supply a config object with a `threshold` property instead of `true`:
         *
         * ```javascript
         * const store = new Store({
         *     syncDataOnLoad : {
         *         threshold : '20%'
         *     }
         * });
         * ```
         *
         * `threshold` accepts numbers or strings. A numeric threshold means number of affected records, while a
         * string is used as a percentage of the whole dataset (appending `%` is optional). By default no threshold
         * is used.
         *
         * #### Missing fields
         *
         * The value of any field not supplied in the new dataset is by default kept as is (if record is not removed
         * by the sync). This behaviour is configurable, by setting `keepMissingValues : false` in a config object
         * it will reset any unspecified field back to their default values:
         *
         * ```javascript
         * const store = new Store({
         *     syncDataOnLoad : {
         *         keepMissingValues : false
         *     }
         * });
         * ```
         *
         * Considering the following sync operation:
         *
         * ```javascript
         * // Existing data
         * { id : 1, name : 'Saitama', powerLevel : 100 }
         * // Sync data
         * { id : 1, name : 'One-Punch Man' }
         * ```
         *
         * The result would by default (or when explicitly configured with `true`)  be:
         *
         * ```javascript
         * { id : 1, name : 'One-Punch Man', powerLevel : 100 }
         * ```
         *
         * If configured with `keepMissingValues : false` it would instead be:
         *
         * ```javascript
         * { id : 1, name : 'One-Punch Man' }
         * ```
         *
         * <div class="note">Never enable `syncDataOnLoad` on a chained store, it will create an infinite loop when
         * it is populated from the main store (the main store can use the setting)</div>
         *
         * @config {Boolean|SyncDataOnLoadOptions} syncDataOnLoad
         * @default false
         * @category Common
         * @non-lazy-load
         */
        syncDataOnLoad: null,
        /**
         * Experimental hook to allow app to determine if sync should be performed, and/or for which records.
         *
         * @config {Function} shouldSyncDataset
         * @param {Object} options
         * @param {Object[]} options.data New dataset, an array of incoming data objects
         * @param {Core.data.Store} options.store This store
         * @param {Core.data.Model[]} options.records Records currently in this store
         * @returns {Set|Boolean} A set of ids to apply changes to, or `false` to skip sync
         * @private
         */
        shouldSyncDataset: null,
        shouldSyncRecord: null,
        /**
         * When using {@link #config-syncDataOnLoad} and the store is neither sorted nor grouped, the order of the
         * incoming dataset is respected. This has a cost, since records in the store might have to be moved around.
         *
         * For stores where the order is not important, this behavior can be disabled to improve performance by
         * configuring `reorderOnSync` as `false`.
         *
         * @default
         * @private
         */
        reorderOnSync: true
      };
    }
    /**
     * Syncs a new dataset against the already loaded one, only applying changes.
     * Not intended to be called directly, please configure store with `syncDataOnLoad: true` and assign to
     * `store.data` as usual instead.
     *
     * ```
     * const store = new Store({
     *    syncDataOnLoad : true,
     *    data : [
     *        // initial data
     *    ]
     * });
     *
     * store.data = [ // new data ]; //  Difference between initial data and new data will be applied
     * ```
     *
     * @param {Object[]|Core.data.Model[]} data New dataset, an array of records or data objects
     * @private
     */
    syncDataset(data) {
      var _a2, _b, _c, _d, _e;
      const me = this, { storage } = me, idsToCheck = (_d = (_a2 = me.shouldSyncDataset) == null ? void 0 : _a2.call(me, { data, store: me, records: me.records })) != null ? _d : (
        // If part of project, it might have an aggregated version of the hook
        (_c = (_b = me.project) == null ? void 0 : _b.shouldSyncDataset) == null ? void 0 : _c.call(_b, { data, store: me, records: me.records })
      );
      if (idsToCheck === false) {
        return;
      }
      me.trigger("startApplyChangeset");
      me.isSyncingDataOnLoad = true;
      const { toAdd, toRemove, toMove, updated, ids } = me.tree ? me.syncTreeDataset(data, idsToCheck) : me.syncFlatDataset(data, idsToCheck);
      let threshold = (_e = me.syncDataOnLoad) == null ? void 0 : _e.threshold, surpassed = false;
      if (threshold) {
        if (typeof threshold === "string") {
          threshold = parseInt(threshold, 10) / 100 * me.count;
        }
        surpassed = toAdd.length + toRemove.length + toMove.length + updated.length > threshold;
      }
      if (me.tree) {
        if (toAdd.length) {
          const added = me.add(toAdd, surpassed);
          added.forEach((node) => node.clearParentId());
        }
        if (toMove.length) {
          for (const { parent, node, index } of toMove) {
            const newParent = me.getById(parent.id);
            newParent.insertChild(node, index);
          }
        }
        me.remove(toRemove, surpassed);
      } else {
        if (surpassed) {
          me.suspendEvents();
        }
        me.remove(toRemove);
        me.add(toAdd);
        if (surpassed) {
          me.resumeEvents();
        }
      }
      if (threshold && !surpassed) {
        updated.forEach(({ record, toSet, wasSet }) => me.onModelChange(record, toSet, wasSet));
      }
      me.acceptChanges();
      const event = { added: toAdd, removed: toRemove, updated, thresholdSurpassed: surpassed };
      if (me.isFiltered && !me.remoteFilter) {
        me.filter({
          silent: me.isRemoteDataLoading
        });
      }
      if (me.isGrouped) {
        me.group(null, null, false, true, me.isRemoteDataLoading);
      } else if (me.isSorted) {
        if (me.remoteSort) {
          storage.replaceValues({
            values: storage.values.sort(me.createSorterFn(me.sorters)),
            silent: true
          });
        } else {
          me.sort();
        }
      } else if (me.reorderOnSync) {
        if (!me.tree) {
          if (storage.values.some((record, index) => ids[record.id] !== index)) {
            storage.replaceValues({
              values: storage.values.sort((a, b) => ids[a.id] - ids[b.id]),
              silent: true
            });
            !surpassed && me.afterPerformSort();
          }
        } else {
          let unsorted = false, i = 0;
          WalkHelper.preWalk(
            me.rootNode,
            (n) => Array.isArray(n.children) && !unsorted ? n.children : null,
            (node) => {
              if (node.id !== ids[i++]) {
                unsorted = true;
              }
            }
          );
          if (unsorted) {
            me.sort((a, b) => ids[a.id] - ids[b.id], void 0, void 0, true);
            me.clearSorters(true);
            !surpassed && me.afterPerformSort();
          }
        }
      }
      if (surpassed) {
        me.trigger("refresh", {
          action: "batch",
          data,
          records: storage.values,
          syncInfo: event
        });
      }
      me.isSyncingDataOnLoad = false;
      me.trigger("endApplyChangeset");
      me.trigger("loadSync", event);
    }
    // Used by syncDataset()
    syncFlatDataset(data, idsToCheck) {
      if (!data) {
        return {
          toRemove: this.records
        };
      }
      const me = this, { idField, allFields } = me.modelClass, toRemove = [], toAdd = [], updated = [], usedIds = {}, ids = {}, limitedSet = idsToCheck instanceof Set;
      const { threshold, keepMissingValues } = me.syncDataOnLoad;
      let hitCount = 0;
      data.forEach((rawData, i) => {
        var _a2, _b;
        rawData = rawData.isModel ? rawData.data : rawData;
        const id = rawData[idField], record = me.getById(id);
        if (!limitedSet || idsToCheck.has(id)) {
          if (record) {
            if (me.shouldSyncRecord ? me.shouldSyncRecord({ record, data: rawData, store: me }) !== false : rawData !== record.data) {
              (_a2 = me.beforeSyncRecord) == null ? void 0 : _a2.call(me, { record, data: rawData });
              if (keepMissingValues === false) {
                for (const field of allFields) {
                  if (!(field.dataSource in rawData) && field.dataSource in record.data) {
                    rawData[field.dataSource] = field.defaultValue;
                  }
                }
              }
              const wasSet = record.set(rawData, null, Boolean(threshold));
              if (wasSet) {
                updated.push({
                  record,
                  wasSet,
                  toSet: rawData
                });
              }
              (_b = me.afterSyncRecord) == null ? void 0 : _b.call(me, { record, data: rawData });
            }
          } else {
            toAdd.push(me.processRecord(me.createRecord(rawData)));
          }
        }
        if (record) {
          hitCount++;
        }
        usedIds[id] = 1;
        ids[id] = i;
      });
      if (hitCount < me.allRecords.filter((x) => !x.isSpecialRow).length) {
        if (idsToCheck) {
          for (const id of idsToCheck) {
            if (!usedIds[id]) {
              toRemove.push(me.getById(id));
            }
          }
        } else {
          me.forEach((record) => {
            if (!usedIds[record.id]) {
              toRemove.push(record);
            }
          }, me, {
            includeFilteredOutRecords: true,
            includeCollapsedGroupRecords: true
          });
        }
      }
      return { toAdd, toRemove, toMove: [], updated, ids };
    }
    // Used by syncDataset()
    syncTreeDataset(data) {
      var _a2;
      if (!data) {
        return {
          toRemove: this.records
        };
      }
      const me = this, {
        idField,
        parentIdField,
        childrenField,
        allFields
      } = me.modelClass, {
        keepMissingValues,
        threshold
      } = (_a2 = me.syncDataOnLoad) != null ? _a2 : {}, toRemove = [], toAdd = [], toMove = [], updated = [], matchedNodes = /* @__PURE__ */ new Set(), ids = {};
      let index = 0;
      if (me.transformFlatData) {
        data = me.treeifyFlatData(data);
      }
      WalkHelper.preWalkWithParent({ isRoot: true, id: me.rootNode.id, children: data }, (n) => n.children, (parent, rawData) => {
        if (parent) {
          const { id, node } = me.resolveSyncNode(rawData);
          if (node) {
            if (me.shouldSyncRecord ? me.shouldSyncRecord({ record: node, data: rawData, store: me }) !== false : rawData !== node.data) {
              let childrenUpdated;
              const oldChildrenValue = node.children;
              if (node.parent.id !== parent[idField]) {
                toMove.push({
                  node,
                  parent,
                  index: parent[childrenField].indexOf(rawData)
                });
              }
              let wasSet;
              if (node !== rawData) {
                if (oldChildrenValue !== true && rawData[childrenField] === true) {
                  node.clearChildren();
                  node.data[childrenField] = node.children = true;
                  delete rawData[childrenField];
                  me.toggleCollapse(node, true);
                  childrenUpdated = true;
                }
                if (keepMissingValues === false) {
                  for (const field of allFields) {
                    if (field.name !== "parentId" && !(field.dataSource in rawData) && field.dataSource in node.data) {
                      rawData[field.dataSource] = field.defaultValue;
                    }
                  }
                }
                if (typeof rawData.expanded === "boolean") {
                  this.toggleCollapse(node, !rawData.expanded);
                }
                wasSet = node.set(rawData, null, Boolean(threshold));
              }
              if (wasSet) {
                updated.push({
                  record: node,
                  wasSet,
                  toSet: rawData
                });
              } else if (childrenUpdated) {
                node.signalNodeChanged({
                  [childrenField]: {
                    value: true,
                    oldValue: oldChildrenValue
                  }
                });
              }
            }
            matchedNodes.add(node);
          } else {
            rawData[parentIdField] = parent[idField];
            if (rawData.isModel) {
              toAdd.push(rawData);
            } else {
              toAdd.push({
                ...rawData,
                ...Array.isArray(rawData[childrenField]) ? { children: [] } : void 0
              });
            }
          }
          ids[id] = index++;
        }
      });
      if (me.totalCount !== matchedNodes.size) {
        me.traverse((node) => {
          if (!matchedNodes.has(node)) {
            toRemove.push(node);
          }
        });
      }
      return { toAdd, toRemove, toMove, updated, ids };
    }
    // ColumnStore overrides this fn to allow syncing by field & type
    resolveSyncNode(rawData) {
      const id = rawData[this.modelClass.idField], node = this.getById(id);
      return { id, node };
    }
  }, __publicField(_a, "$name", "StoreSync"), _a;
};
export {
  StoreSync_default as default
};
